"use client";

import * as React from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { notifications } from "@mantine/notifications";
import { useAuth } from "@bi-studio/shared/hooks";
import { AppLayout, LoadingScreen, ErrorState, ConfirmationModal } from "@bi-studio/shared/components";
import { ReportCreateView } from "@/components/reports";
import { 
    createReport, 
    previewReport, 
    getDataSources, 
    addFolderItem, 
    getSchema 
} from "@bi-studio/shared/apis";
import { useForm } from "@mantine/form";
import { TableSchema } from "@bi-studio/shared";
import { 
    extractVariables, 
    ReportVariable, 
    PREDEFINED_VARIABLES,
    ReportInput,
    DataSource,
    ReportQueryResult
} from "@bi-studio/shared/lib";
import { PreviewVariablesModal } from "@/components/reports/modals/PreviewVariablesModal";
import { PageActions, ReportCreateActionsButton, PageContainer } from "@/components";
import { useAppDispatch, useAppSelector } from "@/store/hooks";
import { 
    setReportDraft, 
    selectReportDraft 
} from "@/store/slices/draftSlice";

export default function CreateReportPage() {
    const { isAuthenticated, isLoading: authLoading, token } = useAuth();
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const folderId = searchParams.get("folderId");
    const reportDraft = useAppSelector(selectReportDraft);

    const [activeTab, setActiveTab] = React.useState<string | null>("general");
    const [dataSources, setDataSources] = React.useState<DataSource[]>([]);
    const [schema, setSchema] = React.useState<TableSchema[]>([]);

    const [isSaving, setIsSaving] = React.useState(false);
    const [error, setError] = React.useState<string | null>(null);

    const form = useForm<ReportInput>({
        initialValues: {
            name: "",
            description: "",
            dataSourceId: "",
            query: "",
            columns: [],
            variables: [],
            allowedRoles: [],
        },
        validate: {
            name: (value) => (value ? null : "Name is required"),
            dataSourceId: (value) => (value ? null : "Data source is required"),
            query: (value) => (value ? null : "Query is required"),
        },
    });

    const [isRestoreModalOpen, setIsRestoreModalOpen] = React.useState(false);

    // Handle draft restoration
    React.useEffect(() => {
        if (reportDraft && form.values.name === "" && form.values.query === "") {
            setIsRestoreModalOpen(true);
        }
    }, []);

    const handleRestoreDraft = () => {
        form.setValues(structuredClone(reportDraft) as ReportInput);
        setIsRestoreModalOpen(false);
    };

    const handleDiscardDraft = () => {
        dispatch(setReportDraft(null));
        setIsRestoreModalOpen(false);
    };

    // Auto-save draft
    React.useEffect(() => {
        const timeoutId = setTimeout(() => {
            if (form.values.name || form.values.query) {
                dispatch(setReportDraft(structuredClone(form.values)));
            }
        }, 2000);
        return () => clearTimeout(timeoutId);
    }, [form.values, dispatch]);

    const [previewResult, setPreviewResult] =
        React.useState<ReportQueryResult | null>(null);
    const [isPreviewing, setIsPreviewing] = React.useState(false);
    const [previewVariablesModalOpened, setPreviewVariablesModalOpened] = React.useState(false);
    const [previewVariableValues, setPreviewVariableValues] = React.useState<Record<string, string>>({});
    const [errorToFix, setErrorToFix] = React.useState<string | null>(null);

    const scanVariables = React.useCallback((query: string) => {
        const detectedNames = extractVariables(query);
        const currentVariables = form.values.variables || [];

        // Remove variables that are no longer in the query
        const existingMap = new Map((currentVariables || []).map(v => [v.name, v]));

        // Create new list preserving existing config where possible
        const newVariables: ReportVariable[] = detectedNames.map(name => {
            if (existingMap.has(name)) {
                return existingMap.get(name)!;
            }
            return {
                name,
                label: name,
                type: 'text', // Default to text
            };
        });

        // Only update if changed deep equality check simple
        if (JSON.stringify(newVariables) !== JSON.stringify(currentVariables)) {
            form.setFieldValue('variables', newVariables);
        }
    }, [form]);

    // Scan variables when query changes (debounced)
    React.useEffect(() => {
        const timeoutId = setTimeout(() => {
            scanVariables(form.values.query);
        }, 1000); // 1s debounce
        return () => clearTimeout(timeoutId);
    }, [form.values.query, scanVariables]);

    // Load data sources
    React.useEffect(() => {
        if (!token) return;
        getDataSources(token)
            .then(setDataSources)
            .catch((err) => console.error("Failed to load data sources:", err));
    }, [token]);

    // Load schema when data source changes
    React.useEffect(() => {
        if (!token || !form.values.dataSourceId) {
            setSchema([]);
            return;
        }

        getSchema(token, form.values.dataSourceId)
            .then(setSchema)
            .catch((err) => {
                console.error("Failed to load schema:", err);
                setSchema([]);
            });
    }, [token, form.values.dataSourceId]);

    const handleLogout = () => {
        navigate("/login");
    };

    if (authLoading) {
        return (
            <AppLayout onLogout={handleLogout} title="Workspace" subtitle="BI Studio">
                <LoadingScreen message="Initializing workspace..." />
            </AppLayout>
        );
    }

    if (!isAuthenticated) {
        return null;
    }

    const handlePreview = async () => {
        setErrorToFix(null);
        if (!token || !form.values.dataSourceId || !form.values.query.trim()) {
            setError("Please select a data source and enter a query");
            return;
        }

        const detectedVariables = extractVariables(form.values.query);
        const userVariables = detectedVariables.filter(v => !PREDEFINED_VARIABLES.includes(v));

        if (userVariables.length > 0) {
            setPreviewVariablesModalOpened(true);
            return;
        }

        await runPreview();
    };

    const runPreview = async (variableValues: Record<string, string> = {}) => {
        if (!token) {
            setError("Authentication token is missing. Please log in again.");
            return;
        }
        try {
            setIsPreviewing(true);
            setError(null);
            const cleanQuery = form.values.query.trim().replace(/;$/, "");
            const result = await previewReport(token, {
                dataSourceId: form.values.dataSourceId,
                query: cleanQuery,
                pagination: { page: 1, pageSize: 100 },
                variables: variableValues,
            });
            setPreviewResult(result);
            setPreviewVariablesModalOpened(false);

            // Update columns state if new columns are detected
            const detectedColumns: any[] = [];
            if (result.columns && result.columns.length > 0) {
                detectedColumns.push(...result.columns);
            } else if (result.rows && result.rows.length > 0) {
                // Fallback: Infer columns from data rows
                const firstRow = result.rows[0];
                Object.keys(firstRow).forEach((key, idx) => {
                    detectedColumns.push({
                        name: key,
                        displayName: key,
                        dataType: 'string',
                        order: idx
                    });
                });
            }

            if (detectedColumns.length > 0) {
                const currentColumns = form.values.columns || [];
                const existingMap = new Map(currentColumns.map(c => [c.name, c]));
                const newColumns = detectedColumns.map(col => {
                    if (existingMap.has(col.name)) {
                        return existingMap.get(col.name)!;
                    }
                    return col;
                });
                form.setFieldValue('columns', newColumns);
            }
        } catch (err) {
            setError(err instanceof Error ? err.message : "Failed to preview query");
        } finally {
            setIsPreviewing(false);
        }
    };

    const handleSave = async () => {
        const validation = form.validate();
        if (validation.hasErrors) {
            const errorMessages = Object.entries(validation.errors)
                .map(([field, msg]) => `${field}: ${msg}`)
                .join(", ");
            
            notifications.show({
                title: "Validation Error",
                message: `Please fix the following: ${errorMessages}`,
                color: "red",
            });
            
            // Switch to general if common fields are missing
            if (validation.errors.name || validation.errors.dataSourceId) {
                setActiveTab("general");
            } else if (validation.errors.query) {
                setActiveTab("query");
            }
            return;
        }

        if (!token || !form.values.name.trim() || !form.values.dataSourceId || !form.values.query.trim()) {
            const missing = [];
            if (!form.values.name.trim()) missing.push("Name");
            if (!form.values.dataSourceId) missing.push("Data Source");
            if (!form.values.query.trim()) missing.push("Query");

            notifications.show({
                title: "Required Fields Missing",
                message: `Please fill in: ${missing.join(", ")}`,
                color: "red",
            });
            return;
        }

        try {
            setIsSaving(true);
            setError(null);

            const sanitizedVariables = (form.values.variables || []).map(v => {
                if (v.type === 'select' && v.selectConfig?.source === 'query' && v.selectConfig.query) {
                    return {
                        ...v,
                        selectConfig: {
                            ...v.selectConfig,
                            query: v.selectConfig.query.trim().replace(/;$/, "")
                        }
                    };
                }
                return v;
            });

            const reportData: ReportInput = {
                name: form.values.name.trim(),
                description: form.values.description?.trim(),
                dataSourceId: form.values.dataSourceId,
                query: form.values.query.trim().replace(/;$/, ""),
                allowedRoles: form.values.allowedRoles,
                variables: sanitizedVariables,
                columns: form.values.columns || [],
            };

            const created = await createReport(token, reportData);
            dispatch(setReportDraft(null)); // Clear draft on success

            // Add to folder if folderId provided (and not "root")
            if (folderId && folderId !== "root") {
                try {
                    await addFolderItem(token, folderId, {
                        itemType: "report",
                        itemId: created.id,
                    });
                } catch (err) {
                    console.error("Failed to add report to folder:", err);
                    // Continue anyway - report was created
                }
            }

            // Redirect back to folders or specific folder
            if (folderId && folderId !== "root") {
                navigate(`/folders/${folderId}`);
            } else {
                navigate("/folders");
            }
        } catch (err) {
            setError(err instanceof Error ? err.message : "Failed to create report");
            setIsSaving(false);
        }
    };

    const handleCancel = () => {
        if (folderId && folderId !== "root") {
            navigate(`/folders/${folderId}`);
        } else {
            navigate("/folders");
        }
    };
    
    const handleBack = handleCancel;

    return (
        <AppLayout
            title="Workspace"
            subtitle="BI Studio"
            onLogout={handleLogout}
        >
            <PageContainer>
                <PageActions 
                    title="Create Report" 
                    onBack={handleBack}
                >
                    <ReportCreateActionsButton
                        handleCancel={handleCancel}
                        handleSave={handleSave}
                        isSaving={isSaving}
                    />
                </PageActions>

                {error && activeTab !== 'preview' && (
                    <ErrorState 
                        title="Failed to create report"
                        message={error || undefined}
                        onAction={() => setError(null)}
                    />
                )}

                <ReportCreateView 
                    isSaving={isSaving}
                    activeTab={activeTab}
                    setActiveTab={setActiveTab}
                    form={form}
                    dataSources={dataSources}
                    schema={schema}
                    error={error}
                    errorToFix={errorToFix}
                    setErrorToFix={setErrorToFix}
                    handlePreview={handlePreview}
                    isPreviewing={isPreviewing}
                    previewResult={previewResult}
                />

                <PreviewVariablesModal
                    opened={previewVariablesModalOpened}
                    onClose={() => setPreviewVariablesModalOpened(false)}
                    variables={form.values.variables || []}
                    values={previewVariableValues}
                    onValuesChange={setPreviewVariableValues}
                    onConfirm={() => runPreview(previewVariableValues)}
                    dataSourceId={form.values.dataSourceId || ""}
                    loading={isPreviewing}
                />

                <ConfirmationModal
                    opened={isRestoreModalOpen}
                    onClose={handleDiscardDraft}
                    onConfirm={handleRestoreDraft}
                    title="Unsaved Draft Found"
                    confirmLabel="Restore"
                    cancelLabel="Discard"
                >
                    You have an unsaved report draft. Would you like to restore it?
                </ConfirmationModal>
            </PageContainer>
        </AppLayout>
    );
}
